import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
    Box, 
    Button, 
    TextField, 
    Typography, 
    Snackbar, 
    Alert, 
    IconButton, 
    Chip, 
    Grid, 
    Paper,
    Drawer,
    Checkbox,
    FormControlLabel,
    List,
    ListItem,
    Divider,
    alpha
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import UndoIcon from '@mui/icons-material/Undo';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import LabelIcon from '@mui/icons-material/Label';
import RouterIcon from '@mui/icons-material/Router';
import renderBlocks from './renderBlocks';
import { publicInstance } from '../axiosInstance';
import DeviceCard from '../InventoryPanel/DeviceCard';
import { DeviceInfo } from '../InventoryPanel/types';

// Function to check if a timestamp is recent (within last 7 days)
const isRecentTimestamp = (timestamp: string | undefined): boolean => {
    if (!timestamp) return false;
    
    try {
        const timestampDate = new Date(timestamp);
        const currentDate = new Date();
        const timeDiff = currentDate.getTime() - timestampDate.getTime();
        const hoursDiff = timeDiff / (1000 * 60 * 60);
        
        // Consider timestamps older than 1 week as not recent
        return hoursDiff <= 168; // 24 hours * 7 days = 168 hours
    } catch (error) {
        console.error('Error parsing timestamp:', error);
        return false;
    }
};

// Function to format MAC address input (add colons automatically)
const formatMacAddress = (input: string): string => {
    // Remove any non-hexadecimal characters
    const cleanedInput = input.replace(/[^0-9A-Fa-f]/g, '');
    
    // Insert colons after every 2 characters (except at the end)
    let formattedMac = '';
    for (let i = 0; i < cleanedInput.length && i < 12; i += 2) {
        formattedMac += cleanedInput.slice(i, i + 2);
        if (i < 10) formattedMac += ':';
    }
    
    return formattedMac;
};

const AddDetails = () => {
    const theme = useTheme();
    const { groupId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [details, setDetails] = useState<Details>({});

    const [currentInputs, setCurrentInputs] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

    const shipmentData = location.state?.shipmentData || [];

    const [availableDevices, setAvailableDevices] = useState<{ [key: string]: DeviceInfo[] }>({});
    const [allSensorboxes, setAllSensorboxes] = useState<DeviceInfo[]>([]);
    const [selectedDevices, setSelectedDevices] = useState<{ [key: string]: string[] }>({});
    const [showManualInput, setShowManualInput] = useState(false);
    const [hasAllDetails, setHasAllDetails] = useState(false);
    const [deviceFilter, setDeviceFilter] = useState<string>('');
    const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
    const [selectedModems, setSelectedModems] = useState<string[]>([]);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // Function to debounce search input for better performance
    function useDebounce<T>(value: T, delay: number): T {
        const [debouncedValue, setDebouncedValue] = useState<T>(value);
        
        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);
            
            return () => {
                clearTimeout(handler);
            };
        }, [value, delay]);
        
        return debouncedValue;
    }

    const debouncedFilter = useDebounce(deviceFilter, 300);

    // Derive unique labels from all devices
    const uniqueLabels = useMemo(() => {
        const labelsSet = new Set<string>();
        
        Object.values(availableDevices).flat().forEach(device => {
            if (device.label) {
                device.label.forEach(labelObj => {
                    if (labelObj.label) {
                        labelsSet.add(labelObj.label);
                    }
                });
            }
        });
        
        return Array.from(labelsSet).sort();
    }, [availableDevices]);
    
    // Derive unique modems from all devices
    const uniqueModems = useMemo(() => {
        const modemsSet = new Set<string>();
        
        Object.values(availableDevices).flat().forEach(device => {
            if (device.modem && device.modem.trim() !== '') {
                const cleanModem = device.modem.replace(/\r|\n/g, '').trim();
                modemsSet.add(cleanModem);
            }
        });
        
        return Array.from(modemsSet).sort();
    }, [availableDevices]);

    useEffect(() => {
        const allFilled = shipmentData.every(item => {
            const currentDetails = details[item.device_type] || [];
            return currentDetails.length === item.quantity;
        });
        setIsSubmitDisabled(!allFilled);
    }, [details, shipmentData]);

    useEffect(() => {
        const fetchAvailableDevices = async () => {
            try {
                const inventoryResponse = await publicInstance.get('/view_dacapi_inventory_all');
                
                const processedData = inventoryResponse.data.map((device: any) => ({
                    box: device.box.toString(),
                    iccid: device.iccid,
                    time_stamp: device.time_stamp,
                    operator: device.mobile_operator 
                        ? (device.mobile_operator === 'ORANGE' ? 'Orange' : 
                           device.mobile_operator === 'PLAY' ? 'Play' : 
                           device.mobile_operator === 'PLUS' ? 'Plus' : device.mobile_operator)
                        : 'Unknown',
                    modem: device.modem,
                    label: Array.isArray(device.label) ? device.label : [],
                    comments: device.comments
                }));

                // Fetch calibration data for all devices
                try {
                    const boxNumbers = processedData.map(device => device.box);
                    
                    if (boxNumbers.length > 0) {
                        const calibrationResponse = await publicInstance.get(`/view_dacapi_calib_info_merged?box=in.(${boxNumbers.join(',')})`);
                        
                        // Add calibration data to each device
                        processedData.forEach(device => {
                            const calibrationData = calibrationResponse.data.find((item: any) => item.box.toString() === device.box);
                            device.calibrations = calibrationData ? calibrationData.calibrations : [];
                        });
                    }
                } catch (calibError) {
                    console.error('Error fetching calibration data:', calibError);
                }

                const sensorboxes = processedData.filter(
                    (d: any) => d.operator === 'Orange' || d.operator === 'Play' || d.operator === 'Plus'
                );
                setAllSensorboxes(sensorboxes);

                // Group devices by operator and sort them (box number ascending)
                const grouped = processedData.reduce((acc: any, device: any) => {
                    const operator = device.operator;
                    if (!acc[operator]) acc[operator] = [];
                    acc[operator].push(device);
                    return acc;
                }, {});

                // Sort devices in each group
                Object.keys(grouped).forEach(operator => {
                    grouped[operator].sort((a: DeviceInfo, b: DeviceInfo) => {
                        return parseInt(a.box) - parseInt(b.box);
                    });
                });

                setAvailableDevices(grouped);
            } catch (error) {
                console.error('Error fetching available devices:', error);
            }
        };

        fetchAvailableDevices();
    }, []);

    useEffect(() => {
        const allComplete = shipmentData.every(item => {
            const currentDetails = details[item.device_type] || [];
            return currentDetails.length >= item.quantity;
        });
        setHasAllDetails(allComplete);
    }, [details, shipmentData]);

    const handleDeviceSelection = (deviceType: string, device: DeviceInfo) => {
        const boxNumber = device.box;
        setSelectedDevices(prev => {
            const currentSelected = prev[deviceType] || [];
            const shipmentItem = shipmentData.find(item => item.device_type === deviceType);
            
            if (currentSelected.includes(boxNumber)) {
                // Deselect device
                const newSelected = currentSelected.filter(num => num !== boxNumber);
                // Also remove from details
                const currentDetails = details[deviceType] || [];
                setDetails({
                    ...details,
                    [deviceType]: currentDetails.filter(d => d.box !== boxNumber)
                });
                return {
                    ...prev,
                    [deviceType]: newSelected
                };
            } else if (currentSelected.length < (shipmentItem?.quantity || 0)) {
                // Select device
                const isAnySensorbox = deviceType === 'sensorbox(ANY)';
                const operator = isAnySensorbox ? device.operator : deviceType.match(/\((.*?)\)/)?.[1];
                const newDetail = {
                    box: boxNumber,
                    mac_address: boxNumber,
                    ...(operator ? { operator } : {})
                };
                
                setDetails(prevDetails => ({
                    ...prevDetails,
                    [deviceType]: [...(prevDetails[deviceType] || []), newDetail]
                }));
                
                return {
                    ...prev,
                    [deviceType]: [...currentSelected, boxNumber]
                };
            }
            return prev;
        });
    };

    const handleDetailChange = (deviceType: string, value: string) => {
        // For p5/p8 device types, automatically format MAC addresses
        if (deviceType === 'p5' || deviceType === 'p8') {
            // Split by whitespace to handle multiple MAC addresses
            const macAddresses = value.split(/\s+/);
            const formattedMacs = macAddresses.map((mac, index) => {
                // Only format the last MAC address being typed
                return index === macAddresses.length - 1 ? formatMacAddress(mac) : mac;
            });
            
            setCurrentInputs({
                ...currentInputs,
                [deviceType]: formattedMacs.join(' '),
            });
        } else {
            setCurrentInputs({
                ...currentInputs,
                [deviceType]: value,
            });
        }
    };

    const handleApply = (deviceType: string) => {
        const currentDetails = details[deviceType] || [];
        const value = currentInputs[deviceType];
        if (!value) return;
        const inputs = value.trim().split(/\s+/).filter(Boolean);

        if (inputs.length === 0) {
            setSnackbarMessage('Please enter a value');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        if (currentDetails.length + inputs.length > shipmentData.find(item => item.device_type === deviceType)?.quantity) {
            setSnackbarMessage(`You cannot add more than the required quantity for ${deviceType}`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        // Validate inputs before adding
        if (deviceType === 'p5' || deviceType === 'p8') {
            // Validate MAC address format for p5/p8
            const macRegex = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/;
            
            // Format incomplete MAC addresses before validation
            const formattedInputs = inputs.map(mac => {
                // If MAC is exactly 12 hex characters without colons, format it
                if (/^[0-9A-Fa-f]{12}$/.test(mac.replace(/:/g, ''))) {
                    return formatMacAddress(mac);
                }
                return mac;
            });
            
            const invalidMacs = formattedInputs.filter(mac => !macRegex.test(mac));
            
            if (invalidMacs.length > 0) {
                setSnackbarMessage(`Invalid MAC address format: ${invalidMacs.join(', ')}. Format should be XX:XX:XX:XX:XX:XX`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }
            
            // Use formatted inputs
            inputs.length = 0;
            inputs.push(...formattedInputs);
        } else {
            // Validate box numbers for sensorboxes
            const numberRegex = /^\d{4}$/;
            const invalidBoxes = inputs.filter(box => !numberRegex.test(box));
            
            if (invalidBoxes.length > 0) {
                setSnackbarMessage(`Invalid box number format: ${invalidBoxes.join(', ')}. Box numbers should be 4 digits.`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }
        }
        
        const operatorToken = deviceType.startsWith('sensorbox') ? deviceType.match(/\(([^)]+)\)/)?.[1] : null;
        if(operatorToken === 'ANY') {
            for (const input of inputs) {
                if (!allSensorboxes.find(d => d.box === input)) {
                    setSnackbarMessage(`Device with box number ${input} not found or already assigned.`);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }
            }
        }

        const newDetailsToAdd = inputs.map(input => {
            let additionalFields = {};
            if (deviceType.startsWith('sensorbox')) {
                let operator = operatorToken;
                if(operator === 'ANY') {
                    const device = allSensorboxes.find(d => d.box === input);
                    operator = device!.operator;
                }
                additionalFields = { operator };
            } else if (deviceType === 'p5' || deviceType === 'p8') {
                additionalFields = { board_type: deviceType };
            }

            if (deviceType === 'p5' || deviceType === 'p8') {
                return { 
                    ...additionalFields, 
                    box: `${deviceType}-${input.replace(/:/g, '')}`, 
                    mac_address: input 
                };
            } else {
                return { 
                    ...additionalFields, 
                    box: input, 
                    mac_address: input 
                };
            }
        });

        const newDetails = [
            ...currentDetails,
            ...newDetailsToAdd
        ];

        setDetails({
            ...details,
            [deviceType]: newDetails,
        });

        setCurrentInputs({
            ...currentInputs,
            [deviceType]: '',
        });
    };

    const handleUndo = (deviceType: string) => {
        const currentDetails = details[deviceType] || [];
        if (currentDetails.length > 0) {
            const lastDetail = currentDetails[currentDetails.length - 1];
            const newDetails = currentDetails.slice(0, -1);
            
            // Update details
            setDetails({
                ...details,
                [deviceType]: newDetails,
            });

            // Update selectedDevices
            setSelectedDevices(prev => ({
                ...prev,
                [deviceType]: (prev[deviceType] || []).filter(box => box !== lastDetail.box)
            }));
        }
    };

    interface Detail {
        box: string;
        mac_address: string;
    }
    
    type Details = {
        [deviceType: string]: Detail[];
    };
    
    
    
    const handleEdit = (deviceType: string, index: number, newValue: string) => {
        const currentDetails = details[deviceType] || [];
        const updatedDetails = currentDetails.map((detail, idx) => {
            if (idx === index) {
                return { ...detail, box: newValue, mac_address: newValue };
            }
            return detail;
        }).filter(detail => detail.box || detail.mac_address); 
    
        setDetails(prevDetails => {
            if (updatedDetails.length === 0) {
               
                const { [deviceType]: _, ...rest } = prevDetails;
                return rest;
            }
            return {
                ...prevDetails,
                [deviceType]: updatedDetails,
            };
        });
    };
    
    

    const validateDetails = () => {
        const seenNumbers = new Set();
        const seenMacs = new Set();

        for (const [deviceType, deviceDetails] of Object.entries(details)) {
            for (const detail of deviceDetails as any[]) {
                if (deviceType === 'p5' || deviceType === 'p8') {
                    const macRegex = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/;
                    if (!macRegex.test(detail.mac_address)) {
                        return `Invalid MAC address format for ${deviceType}: ${detail.mac_address}`;
                    }
                    if (seenMacs.has(detail.mac_address)) {
                        return `Duplicate MAC address found for ${deviceType}: ${detail.mac_address}`;
                    }
                    seenMacs.add(detail.mac_address);
                } else {
                    const numberRegex = /^\d{4}$/;
                    if (!numberRegex.test(detail.box)) {
                        return `Invalid number format for ${deviceType}: ${detail.box}`;
                    }
                    if (seenNumbers.has(detail.box)) {
                        return `Duplicate number found for ${deviceType}: ${detail.box}`;
                    }
                    seenNumbers.add(detail.box);
                }
            }
        }
        return null;
    };

    const handleSubmit = async () => {
        const validationError = validateDetails();
        if (validationError) {
            setSnackbarMessage(validationError);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        const payload = {
            details: Object.values(details).flat(),
            status: "skompletowane"
        };
        try {
            await publicInstance.patch(`/dacapi_shipment_of_devices?group_id=eq.${groupId}`, payload);
            navigate('/shipments', {
                state: {
                    snackbarOpen: true,
                    snackbarMessage: 'Details added successfully',
                    snackbarSeverity: 'success',
                },
            });
        } catch (error) {
            console.error('Error adding details:', error);
            navigate('/shipments', {
                state: {
                    snackbarOpen: true,
                    snackbarMessage: 'Error adding details',
                    snackbarSeverity: 'error',
                },
            });
        }
    };

    const handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const disabledButtonStyles = theme.name === 'dark' ? {
        backgroundColor: 'white',
        color: 'white',
    } : {};

    const getOperatorColor = (operator: string) => {
        switch (operator) {
            case 'Orange':
                return 'orange';
            case 'Play':
                return 'purple';
            case 'Plus':
                return 'green';
            default:
                return 'grey';
        }
    };

    // Filter and sort devices based on search input
    const getFilteredDevices = useCallback((devices: DeviceInfo[], filter: string) => {
        // First filter by search term, labels and modems
        const filtered = devices.filter(device => {
            // Filter by box number
            if (filter && !device.box.includes(filter)) {
                return false;
            }
            
            // Filter by labels if any are selected
            if (selectedLabels.length > 0) {
                if (!device.label || device.label.length === 0) {
                    return false;
                }
                
                const deviceLabelNames = device.label.map(l => l.label);
                if (!selectedLabels.some(selectedLabel => 
                    deviceLabelNames.includes(selectedLabel)
                )) {
                    return false;
                }
            }
            
            // Filter by modems if any are selected
            if (selectedModems.length > 0) {
                if (!device.modem || device.modem.trim() === '') {
                    return false;
                }
                
                const cleanModem = device.modem.replace(/\r|\n/g, '').trim();
                if (!selectedModems.includes(cleanModem)) {
                    return false;
                }
            }
            
            return true;
        });
        
        // Then sort according to the same rules as in InventoryPanel
        return [...filtered].sort((a, b) => {
            // First, sort by recent activity
            const aIsRecent = isRecentTimestamp(a.time_stamp);
            const bIsRecent = isRecentTimestamp(b.time_stamp);
            if (aIsRecent !== bIsRecent) {
                return aIsRecent ? -1 : 1;
            }

            // Then sort by presence of labels
            const aHasLabels = a.label && a.label.length > 0;
            const bHasLabels = b.label && b.label.length > 0;
            if (aHasLabels !== bHasLabels) {
                return aHasLabels ? -1 : 1;
            }

            // If both have labels, sort by number of labels
            if (aHasLabels && bHasLabels && a.label!.length !== b.label!.length) {
                return b.label!.length - a.label!.length;
            }

            // Finally, sort by box number
            return parseInt(a.box) - parseInt(b.box);
        });
    }, [selectedLabels, selectedModems]);

    // Handle toggling a label
    const handleLabelToggle = useCallback((label: string) => {
        setSelectedLabels(prev => 
            prev.includes(label)
                ? prev.filter(l => l !== label)
                : [...prev, label]
        );
    }, []);

    // Handle toggling a modem
    const handleModemToggle = useCallback((modem: string) => {
        setSelectedModems(prev => 
            prev.includes(modem)
                ? prev.filter(m => m !== modem)
                : [...prev, modem]
        );
    }, []);

    // Clear all filters
    const clearFilters = useCallback(() => {
        setDeviceFilter('');
        setSelectedLabels([]);
        setSelectedModems([]);
    }, []);

    return (
        <Box sx={{ padding: 2 }}>
            <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                sx={{ marginBottom: 2 }}
            >
                Back
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ 
                    width: 'auto',
                    maxWidth: '100%',
                    pr: 2
                }}>
                    <Typography variant="h5" gutterBottom>Add Details for Group ID: {groupId}</Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowManualInput(!showManualInput)}
                        >
                            {showManualInput ? 'Hide Manual Input' : 'Show Manual Input'}
                        </Button>
                        
                        {/* Global search box for all devices */}
                        <TextField
                            placeholder="Search box number..."
                            variant="outlined"
                            size="small"
                            value={deviceFilter}
                            onChange={(e) => setDeviceFilter(e.target.value)}
                            sx={{ width: 250 }}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon color="action" sx={{ mr: 0.5, fontSize: '1rem' }} />
                                ),
                            }}
                        />
                        
                        {/* Filter button */}
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<FilterListIcon />}
                            onClick={() => setFilterDrawerOpen(true)}
                            sx={{ 
                                ml: 1,
                                backgroundColor: (selectedLabels.length > 0 || selectedModems.length > 0) 
                                    ? theme.palette.primary.light 
                                    : 'transparent',
                                color: (selectedLabels.length > 0 || selectedModems.length > 0)
                                    ? theme.palette.primary.contrastText
                                    : theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: (selectedLabels.length > 0 || selectedModems.length > 0)
                                        ? theme.palette.primary.main
                                        : alpha(theme.palette.primary.main, 0.1)
                                }
                            }}
                        >
                            Filters
                            {(selectedLabels.length > 0 || selectedModems.length > 0) && (
                                <Box 
                                    component="span" 
                                    sx={{ 
                                        ml: 1,
                                        backgroundColor: theme.palette.primary.contrastText,
                                        color: theme.palette.primary.main,
                                        borderRadius: '50%',
                                        width: 20,
                                        height: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {selectedLabels.length + selectedModems.length}
                                </Box>
                            )}
                        </Button>
                    </Box>

                    {shipmentData.map((item: any) => {
                        const operator = item.device_type.match(/\((.*?)\)/)?.[1] || '';
                        const selectedForType = selectedDevices[item.device_type] || [];
                        const isAnySensorbox = operator === 'ANY';

                        const allSelectedBoxes = Object.values(selectedDevices).flat();
                        
                        let availableForOperator;
                        if(isAnySensorbox) {
                            availableForOperator = allSensorboxes.filter(d => !allSelectedBoxes.includes(d.box))
                        } else {
                            availableForOperator = (availableDevices[operator] || []).filter(d => !allSelectedBoxes.includes(d.box));
                        }
                        
                        // Filter devices based on search input
                        const filteredDevices = getFilteredDevices(availableForOperator, debouncedFilter);

                        return (
                            <Box key={item.device_type} sx={{ mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    {item.device_type} (Selected: {(details[item.device_type] || []).length}/{item.quantity})
                                </Typography>
                                
                                {/* Only show device cards for non-p5/p8 devices */}
                                {item.device_type !== 'p5' && item.device_type !== 'p8' && (
                                    <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                            gap: 1,
                                            width: '100%',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            pb: 2,
                                            pt: 1,
                                            px: 1,
                                        }}
                                    >
                                        {filteredDevices.map((device: any) => (
                                            <DeviceCard
                                                key={device.box}
                                                device={device}
                                                selectionMode={true}
                                                isSelected={selectedForType.includes(device.box)}
                                                compact={false}
                                                onClick={() => handleDeviceSelection(item.device_type, device)}
                                            />
                                        ))}
                                    </Grid>
                                )}

                                {/* For p5/p8, display instruction text */}
                                {(item.device_type === 'p5' || item.device_type === 'p8') && (
                                    <Box sx={{ mb: 2, mt: 1, p: 2, bgcolor: theme.palette.background.paper, borderRadius: 1, border: `1px dashed ${theme.palette.divider}` }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Please enter MAC addresses for {item.device_type} devices.
                                            <br />
                                            You can type just the hexadecimal digits (e.g., <code>AABBCCDDEEFF</code>) - colons will be added automatically.
                                            <br />
                                            Separate multiple MAC addresses with spaces.
                                        </Typography>
                                    </Box>
                                )}

                                {/* Always show manual input for p5/p8 devices, or when showManualInput is true for other devices */}
                                {(item.device_type === 'p5' || item.device_type === 'p8' || showManualInput) && (
                                    <Box sx={{ display: 'flex', gap: 1, marginBottom: 1 }}>
                                        {(details[item.device_type] || []).length < item.quantity && (
                                            <TextField
                                                label={`Enter ${item.device_type === 'p5' || item.device_type === 'p8' ? 'MAC addresses' : 'numbers'} for ${item.device_type} (separated by spaces)`}
                                                size="small"
                                                multiline
                                                minRows={3}
                                                value={currentInputs[item.device_type] || ''}
                                                sx={{ maxWidth: 250 }}
                                                onChange={(e) => handleDetailChange(
                                                    item.device_type,
                                                    e.target.value
                                                )}
                                            />
                                        )}
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() => handleApply(item.device_type)}
                                            disabled={(details[item.device_type] || []).length >= item.quantity}
                                            sx={{
                                                ...((details[item.device_type] || []).length >= item.quantity ? disabledButtonStyles : {}),
                                                '&.Mui-disabled': {
                                                    backgroundColor: theme.name === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '',
                                                    color: theme.name === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '',
                                                    opacity: 1,
                                                },
                                            }}
                                        >
                                            Apply
                                        </Button>
                                        <IconButton
                                            aria-label="undo"
                                            onClick={() => handleUndo(item.device_type)}
                                            disabled={(details[item.device_type] || []).length === 0}
                                            sx={(details[item.device_type] || []).length === 0 ? disabledButtonStyles : {}}
                                        >
                                            <UndoIcon />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}

                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        sx={{
                            '&.Mui-disabled': {
                                backgroundColor: theme.name === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '',
                                color: theme.name === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '',
                                opacity: 1,
                            },
                        }}
                    >
                        Submit Details
                    </Button>

                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={6000}
                        onClose={handleSnackbarClose}
                    >
                        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </Box>
                <Box sx={{ 
                    width: '50%',
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'flex-start',
                    mt: 10 
                }}>
                    <Box>
                        {shipmentData.map((item: any, idx: number) => {
                            let color, isP5 = false, isP8 = false;
                            const deviceNumbers = (details[item.device_type] || []).map((d: any) => d.box || d.mac_address || '');

                            switch (item.device_type) {
                                case 'sensorbox(Orange)':
                                    color = 'orange';
                                    break;
                                case 'sensorbox(Play)':
                                    color = 'purple';
                                    break;
                                case 'sensorbox(Plus)':
                                    color = 'green';
                                    break;
                                case 'p5':
                                    color = "black";
                                    isP5 = true;
                                    break;
                                case 'p8':
                                    color = "black";
                                    isP8 = true;
                                    break;
                                case 'sensorbox(ANY)':
                                    color = 'grey';
                                    break;
                                default:
                                    color = 'grey';
                            }
                            return (
                                <Box key={idx}>
                                    {renderBlocks(item.quantity, color, isP5, isP8, true, deviceNumbers, (index, newValue) => handleEdit(item.device_type, index, newValue))}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
            
            {/* Filter Drawer */}
            <Drawer
                anchor="right"
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
            >
                <Box sx={{ width: 300, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Filters</Typography>
                        <Button 
                            variant="text" 
                            size="small"
                            onClick={clearFilters}
                            disabled={selectedLabels.length === 0 && selectedModems.length === 0}
                        >
                            Clear All
                        </Button>
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Labels section */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LabelIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                            Labels {selectedLabels.length > 0 && `(${selectedLabels.length})`}
                        </Typography>
                        
                        <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                            {uniqueLabels.map(label => (
                                <ListItem key={label} dense>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedLabels.includes(label)}
                                                onChange={() => handleLabelToggle(label)}
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {label}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {uniqueLabels.length === 0 && (
                                <ListItem>
                                    <Typography variant="body2" color="text.secondary">
                                        No labels available
                                    </Typography>
                                </ListItem>
                            )}
                        </List>
                        
                        {selectedLabels.length > 0 && (
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                onClick={() => setSelectedLabels([])}
                                sx={{ mt: 1 }}
                            >
                                Clear Labels
                            </Button>
                        )}
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Modems section */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <RouterIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                            Modems {selectedModems.length > 0 && `(${selectedModems.length})`}
                        </Typography>
                        
                        <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                            {uniqueModems.map(modem => (
                                <ListItem key={modem} dense>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedModems.includes(modem)}
                                                onChange={() => handleModemToggle(modem)}
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {modem.length > 20 ? `${modem.substring(0, 18)}...` : modem}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {uniqueModems.length === 0 && (
                                <ListItem>
                                    <Typography variant="body2" color="text.secondary">
                                        No modems available
                                    </Typography>
                                </ListItem>
                            )}
                        </List>
                        
                        {selectedModems.length > 0 && (
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                onClick={() => setSelectedModems([])}
                                sx={{ mt: 1 }}
                            >
                                Clear Modems
                            </Button>
                        )}
                    </Box>
                    
                    
                </Box>
            </Drawer>
        </Box>
    );
};

export default AddDetails; 